import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import Media from 'react-media';
import StatementPositiveSolid from '@simplywallst/ui-core/icons/StatementPositiveSolid';
import StatementNegativeSolid from '@simplywallst/ui-core/icons/StatementNegativeSolid';
import Tooltip from '@/components/Tooltip';
import { MarkContainer, ScoreWrapper, DescriptionWrapper } from './styled';
import type { Mark } from '@/pages/CompanyReport/redux/interface';
import { useTheme } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';

interface ScoreValue {
  mark: Mark;
  description?: ReactNode;
  hash?: string;
}

export interface ScoreProps {
  values?: ScoreValue[];
  score?: number;
}

type PassOrFailProps = Pick<ScoreValue, 'mark'> & {
  onClick: () => void;
};
const PassOrFail = forwardRef<HTMLDivElement, PassOrFailProps>((props, ref) => {
  const { mark, onClick, ...rest } = props;
  const theme = useTheme();
  return (
    <Media queries={{ small: { maxWidth: theme.smMax } }}>
      {(matches) => {
        return (
          <MarkContainer
            onClick={() => {
              if (!matches.small) {
                onClick();
              }
            }}
            ref={ref}
            {...rest}
          >
            {mark === 'PASS' ? (
              <StatementPositiveSolid
                data-cy-id="pass"
                fill={theme.color.good}
              />
            ) : (
              <StatementNegativeSolid
                data-cy-id="fail"
                fill={theme.color.bad}
              />
            )}
          </MarkContainer>
        );
      }}
    </Media>
  );
});

const Score = ({ values }: ScoreProps) => {
  const tracking = useTracking();
  const scroller = useScrollIntoView();
  const theme = useTheme();
  if (typeof values === 'undefined') return null;

  return (
    <ScoreWrapper data-cy-id="score">
      {values.map((value, i) => {
        const { mark, description, hash } = value;
        return (
          <Tooltip
            title={<DescriptionWrapper>{description}</DescriptionWrapper>}
            onOpen={() => {
              tracking.trackEvent({
                action: 'hover',
                modifier: 'analysis_checks',
                properties: hash,
              });
            }}
            key={i}
            offsetY={theme.x2spacePx}
          >
            <PassOrFail
              mark={mark}
              onClick={() => {
                if (hash) {
                  scroller(`#${hash}`);
                  tracking.trackEvent({
                    action: 'click',
                    modifier: 'analysis_checks',
                    properties: hash,
                  });
                }
              }}
            />
          </Tooltip>
        );
      })}
    </ScoreWrapper>
  );
};

export default Score;
